import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { FormField } from '@/components/ui/FormField'

export default function Settings() {
  const { profile, refreshProfile, signOut } = useAuth()
  const navigate = useNavigate()

  const [fullName,    setFullName]    = useState(profile?.full_name ?? '')
  const [phone,       setPhone]       = useState(profile?.phone ?? '')
  const [saving,      setSaving]      = useState(false)
  const [saveMsg,     setSaveMsg]     = useState('')
  const [saveError,   setSaveError]   = useState('')

  const [showDelete,  setShowDelete]  = useState(false)
  const [deleteInput, setDeleteInput] = useState('')
  const [deleting,    setDeleting]    = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const handleSaveProfile = async () => {
    if (saving) return
    setSaveMsg(''); setSaveError('')
    setSaving(true)
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: fullName.trim(), phone: phone.trim() || null })
        .eq('id', profile!.id)
      if (error) { setSaveError('Could not save changes.'); return }
      await refreshProfile()
      setSaveMsg('Changes saved.')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteInput !== 'DELETE') { setDeleteError('Type DELETE to confirm.'); return }
    setDeleting(true)
    setDeleteError('')
    try {
      const { error } = await supabase.functions.invoke('delete-account')
      if (error) { setDeleteError('Could not delete account. Please try again.'); return }
      // Auth user is now gone — clear local session and redirect
      await supabase.auth.signOut()
      navigate('/login?notice=account_deleted', { replace: true })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>Settings</div>
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 28 }}>
        Manage your account details
      </div>

      {/* Profile section */}
      <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '22px 22px', marginBottom: 18 }}>
        <div style={{ font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 16 }}>Profile</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FormField label='Full name' value={fullName} onChange={e => setFullName(e.target.value)} placeholder='Your name' />
          <FormField label='Phone'     value={phone}    onChange={e => setPhone(e.target.value)}    placeholder='(416) 555-0100' type='tel' />
          <div>
            <label style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', display: 'block', marginBottom: 6 }}>Email</label>
            <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '11px 13px', background: 'rgba(27,42,74,0.04)', borderRadius: 9 }}>
              {profile?.email ?? '—'}
            </div>
          </div>
          {saveMsg   && <div className='banner-success'>{saveMsg}</div>}
          {saveError && <div className='banner-error'>{saveError}</div>}
          <button className='btn-teal' onClick={handleSaveProfile} disabled={saving} style={{ width: 'auto', padding: '11px 22px' }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* Danger zone */}
      <div style={{ background: '#fff', border: '1px solid rgba(224,102,59,0.25)', borderRadius: 14, padding: '22px 22px' }}>
        <div style={{ font: '600 15px Inter, sans-serif', color: '#E0663B', marginBottom: 6 }}>Danger zone</div>
        <div style={{ font: '400 13px/1.6 Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 14 }}>
          Deleting your account is permanent. Your data cannot be recovered.
        </div>

        {!showDelete ? (
          <button
            onClick={() => setShowDelete(true)}
            style={{ padding: '10px 18px', background: 'transparent', border: '1px solid rgba(224,102,59,0.4)', borderRadius: 9, font: '500 13.5px Inter, sans-serif', color: '#E0663B', cursor: 'pointer' }}
          >
            Delete my account
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className='banner-error'>
              This will sign you out and flag your account for deletion. Type <strong>DELETE</strong> to confirm.
            </div>
            <FormField
              label='Type DELETE to confirm'
              value={deleteInput}
              onChange={e => setDeleteInput(e.target.value)}
              placeholder='DELETE'
            />
            {deleteError && <div className='banner-error'>{deleteError}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                style={{ padding: '10px 18px', background: '#E0663B', border: 'none', borderRadius: 9, font: '500 13.5px Inter, sans-serif', color: '#fff', cursor: 'pointer', opacity: deleting ? 0.6 : 1 }}
              >
                {deleting ? 'Processing…' : 'Confirm delete'}
              </button>
              <button
                onClick={() => { setShowDelete(false); setDeleteInput(''); setDeleteError('') }}
                style={{ padding: '10px 18px', background: 'rgba(27,42,74,0.07)', border: 'none', borderRadius: 9, font: '500 13.5px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
